import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { Button } from '../core/Button';
import { Icon, type IconName } from '../core/Icon';
import { OverflowMenu, type OverflowMenuAction } from '../core/OverflowMenu';
import { useTranslation } from '../data/translations';

export type MissionRowState = 'todo' | 'done' | 'deferred' | 'disabled';
export type MissionRowVariant = 'desktop' | 'mobile';

export interface MissionRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  readonly index?: string;
  readonly icon?: IconName;
  readonly category?: string;
  readonly title?: string;
  readonly time?: string;
  readonly duration?: string;
  readonly priority?: string;
  readonly actionLabel?: string;
  readonly done?: boolean;
  readonly state?: MissionRowState;
  readonly variant?: MissionRowVariant;
  readonly onAction?: () => void;
  /** Complétion directe, sans passer par « Démarrer » — réutilise strictement la même fonction
   * métier que l'action principale « Terminer » (statut `in_progress`) : jamais un second mécanisme
   * de complétion. Rendue en bouton visible (sauf si l'action principale est déjà « Terminer », pour
   * ne jamais dupliquer le même contrôle) et déclenchable par double-clic sur la ligne (voir revue
   * « navigation + validation rapide des missions »). */
  readonly onComplete?: () => void;
  readonly completeLabel?: string;
  readonly showTimes?: boolean;
  readonly dimPriorities?: boolean;
  readonly dimDurations?: boolean;
  readonly style?: CSSProperties | undefined;
  /** Mission hors du mode actif : reste affichée, jamais masquée — seulement signalée. */
  readonly activeForMode?: boolean;
  /** Remplace le bouton d'action principal par un texte factuel (« Reportée au ... »). */
  readonly postponedLabel?: string;
  readonly busy?: boolean;
  /** Contenu du panneau de détail (objectif, instructions...) — laissé libre, MissionRow ignore sa forme. */
  readonly detail?: ReactNode;
  readonly expanded?: boolean;
  readonly onToggleExpanded?: () => void;
  readonly secondaryActions?: readonly OverflowMenuAction[];
  readonly reordering?: boolean;
  readonly position?: number;
  readonly total?: number;
  readonly onMoveUp?: () => void;
  readonly onMoveDown?: () => void;
  readonly dragHandleProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

const cell: CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 12,
  fontWeight: 300,
  color: 'var(--text-muted)',
  letterSpacing: '.02em',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
};

const num: CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 13,
  letterSpacing: '.08em',
  color: 'var(--accent)',
  width: 24,
  flex: '0 0 auto',
};

/**
 * Ligne de mission, partagée par desktop et smartphone. Étend le contenu du pack V7 pour
 * porter les fonctionnalités réelles de l'application : réorganisation (glisser-déposer +
 * clavier), accordéon de détail, menu d'actions secondaires, mission hors mode, report.
 */
export function MissionRow({
  index,
  icon = 'mental',
  category,
  title,
  time,
  duration,
  priority,
  actionLabel,
  done = false,
  state,
  variant = 'desktop',
  onAction,
  onComplete,
  completeLabel = 'Terminer',
  showTimes = true,
  dimPriorities = false,
  dimDurations = false,
  style,
  activeForMode = true,
  postponedLabel,
  busy = false,
  detail,
  expanded = false,
  onToggleExpanded,
  secondaryActions = [],
  reordering = false,
  position,
  total,
  onMoveUp,
  onMoveDown,
  dragHandleProps,
  ...rest
}: MissionRowProps) {
  const { t } = useTranslation();
  const resolvedActionLabel = actionLabel ?? t('status.pending');
  const resolvedState = state ?? (done ? 'done' : 'todo');
  const isDone = resolvedState === 'done';
  const isDeferred = resolvedState === 'deferred';
  const isOff = resolvedState === 'disabled';
  const dim = isDone || isDeferred || isOff;
  const missionLabel = title ? `la mission ${title}` : 'cette mission';
  const canExpand = Boolean(detail) && !reordering;
  /** Complétion disponible : jamais sur une mission déjà terminée, reportée, désactivée ou en cours
   * de réorganisation — mêmes règles que celles qui gouvernent déjà l'action principale. */
  const canComplete = Boolean(onComplete) && !isDone && !isDeferred && !isOff && !reordering;
  /** N'affiche le bouton TERMINER dédié que si l'action principale n'est pas déjà « Terminer »
   * (statut en cours) — sinon ce serait un second bouton strictement identique au premier. */
  const showCompleteButton = canComplete && resolvedActionLabel !== completeLabel;

  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '0 var(--pad-row-x)',
    borderBottom: '1px solid rgba(255,255,255,.11)',
    transition: 'var(--t-hover)',
    opacity: isOff ? 0.4 : 1,
  };
  const isOpen = canExpand && expanded;

  const header =
    variant === 'mobile' ? (
      <>
        <span style={num}>{index}</span>
        <span style={{ color: 'var(--accent)', display: 'flex' }}>
          <Icon name={icon} size={20} />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 17,
            fontWeight: 300,
            color: dim ? 'var(--text-faint)' : 'var(--text-display)',
            textDecoration: isDone ? 'line-through' : 'none',
          }}
        >
          {title}
        </span>
        {!activeForMode && <ModeTag />}
        {showTimes && (
          <span className={dimDurations ? 'ai-dimmed' : ''} style={cell}>
            {duration}
          </span>
        )}
        {canExpand && (
          <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
            <Icon name={expanded ? 'replier' : 'derouler'} size={18} />
          </span>
        )}
      </>
    ) : (
      <>
        <span style={num}>{index}</span>
        <span style={{ color: 'var(--accent)', display: 'flex' }}>
          <Icon name={icon} size={20} />
        </span>
        <span style={{ flex: '1 1 auto', minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontFamily: 'var(--font-ui)',
              fontSize: 9,
              letterSpacing: 'var(--tracking-label)',
              textTransform: 'uppercase',
              color: 'var(--text-faint)',
            }}
          >
            {category}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'var(--font-display)',
              fontSize: 17,
              fontWeight: 300,
              color: dim ? 'var(--text-faint)' : 'var(--text-display)',
              textDecoration: isDone ? 'line-through' : 'none',
            }}
          >
            {title}
            {!activeForMode && <ModeTag />}
          </span>
        </span>
        {showTimes && (
          <span className={dimDurations ? 'ai-dimmed' : ''} style={{ ...cell, minWidth: 52, textAlign: 'right' }}>
            {time}
          </span>
        )}
        {showTimes && (
          <span className={dimDurations ? 'ai-dimmed' : ''} style={{ ...cell, minWidth: 54, textAlign: 'right' }}>
            {duration}
          </span>
        )}
        <span
          className={dimPriorities ? 'ai-dimmed' : ''}
          style={{
            ...cell,
            minWidth: 96,
            textTransform: 'uppercase',
            fontSize: 10,
            letterSpacing: '.1em',
            color: priority === 'ESSENTIEL' ? 'var(--text-body)' : 'var(--text-faint)',
          }}
        >
          {priority}
        </span>
        {canExpand && (
          <span style={{ color: 'var(--text-muted)', display: 'flex' }}>
            <Icon name={expanded ? 'replier' : 'derouler'} size={18} />
          </span>
        )}
      </>
    );

  return (
    <div
      {...rest}
      className={isOpen ? 'ai-row ai-row--open' : 'ai-row'}
      style={{
        display: 'grid',
        background: isOpen ? 'var(--surface-raised)' : undefined,
        borderBottom: isOpen ? '1px solid var(--accent-line)' : undefined,
        ...style,
      }}
      aria-label={variant === 'mobile' ? undefined : title}
      onDoubleClick={(event) => {
        if (!canComplete) return;
        /* Ignore le double-clic s'il vise un contrôle interactif interne (action principale,
           TERMINER, menu « ... », poignée/boutons de réorganisation) — seul un double-clic sur la
           ligne elle-même (titre, catégorie, zone d'expansion...) doit terminer la mission. */
        if ((event.target as HTMLElement).closest('[data-mission-control]')) return;
        onComplete?.();
      }}
    >
      {canExpand ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="ai-focusable"
          style={{
            ...base,
            minHeight: variant === 'mobile' ? 'var(--row-h-mobile)' : 'var(--row-h-desktop)',
            width: '100%',
            background: 'none',
            border: 0,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
          }}
        >
          {header}
        </button>
      ) : (
        <div
          style={{
            ...base,
            minHeight: variant === 'mobile' ? 'var(--row-h-mobile)' : 'var(--row-h-desktop)',
          }}
        >
          {header}
        </div>
      )}

      {reordering && position !== undefined && total !== undefined ? (
        <div style={{ ...base, minHeight: 44, gap: 12 }}>
          <button
            type="button"
            aria-label={`Déplacer ${missionLabel}`}
            className="ai-focusable"
            data-mission-control="drag"
            {...dragHandleProps}
            style={{
              width: 32,
              height: 32,
              display: 'inline-grid',
              placeItems: 'center',
              borderRadius: 'var(--r-sm)',
              border: '1px solid rgba(255,255,255,.08)',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'grab',
              flex: '0 0 auto',
            }}
          >
            <Icon name="reorganiser" size={16} />
          </button>
          <span style={{ ...cell, flex: '1 1 auto' }}>
            Position {position} sur {total}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={onMoveUp}
            disabled={position === 1}
            aria-label={`Monter ${missionLabel}`}
            data-mission-control="reorder"
          >
            Monter
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onMoveDown}
            disabled={position === total}
            aria-label={`Descendre ${missionLabel}`}
            data-mission-control="reorder"
          >
            Descendre
          </Button>
        </div>
      ) : null}

      {!reordering && !canExpand ? (
        <RowFooter
          postponedLabel={postponedLabel}
          isOff={isOff}
          busy={busy}
          actionLabel={resolvedActionLabel}
          missionLabel={missionLabel}
          onAction={onAction}
          onComplete={showCompleteButton ? onComplete : undefined}
          completeLabel={completeLabel}
          secondaryActions={secondaryActions}
          padded
        />
      ) : null}

      {expanded && canExpand ? (
        <div style={{ padding: '0 var(--pad-row-x) 18px' }}>
          {!activeForMode ? (
            <p
              style={{
                margin: '0 0 12px',
                fontFamily: 'var(--font-ui)',
                fontSize: 13,
                color: 'var(--text-muted)',
              }}
            >
              Hors du mode actuel : cette mission reste disponible mais ne compte pas dans la
              progression du jour.
            </p>
          ) : null}
          {detail}
          {!reordering ? (
            <RowFooter
              postponedLabel={postponedLabel}
              isOff={isOff}
              busy={busy}
              actionLabel={resolvedActionLabel}
              missionLabel={missionLabel}
              onAction={onAction}
              onComplete={showCompleteButton ? onComplete : undefined}
              completeLabel={completeLabel}
              secondaryActions={secondaryActions}
              padded={false}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ModeTag() {
  const { t } = useTranslation();
  return (
    <span
      style={{
        fontFamily: 'var(--font-ui)',
        fontSize: 9,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
        color: 'var(--text-faint)',
        border: '1px solid rgba(255,255,255,.12)',
        borderRadius: 'var(--r-pill)',
        padding: '2px 8px',
        flex: '0 0 auto',
      }}
    >
      {t('badge.out_of_mode')}
    </span>
  );
}

interface RowFooterProps {
  readonly postponedLabel: string | undefined;
  readonly isOff: boolean;
  readonly busy: boolean;
  readonly actionLabel: string;
  readonly missionLabel: string;
  readonly onAction: (() => void) | undefined;
  /** Présent uniquement quand un bouton TERMINER dédié doit être affiché (voir `showCompleteButton`
   * dans `MissionRow`) — `undefined` masque le bouton sans dupliquer de logique ici. */
  readonly onComplete: (() => void) | undefined;
  readonly completeLabel: string;
  readonly secondaryActions: readonly OverflowMenuAction[];
  readonly padded: boolean;
}

function RowFooter({
  postponedLabel,
  isOff,
  busy,
  actionLabel,
  missionLabel,
  onAction,
  onComplete,
  completeLabel,
  secondaryActions,
  padded,
}: RowFooterProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 10,
        padding: padded ? '10px var(--pad-row-x) 14px' : '14px 0 0',
      }}
    >
      {postponedLabel ? (
        <span style={{ ...cell, flex: '1 1 auto', textAlign: 'left' }}>{postponedLabel}</span>
      ) : (
        <>
          <Button
            size="sm"
            variant={isOff ? 'ghost' : 'secondary'}
            disabled={isOff || busy}
            onClick={onAction}
            aria-label={`${actionLabel} ${missionLabel}`}
            data-mission-control="action"
          >
            {actionLabel}
          </Button>
          {onComplete ? (
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={onComplete}
              aria-label={`${completeLabel} ${missionLabel}`}
              data-mission-control="complete"
            >
              {completeLabel}
            </Button>
          ) : null}
        </>
      )}
      <span data-mission-control="menu" style={{ display: 'contents' }}>
        <OverflowMenu
          label={`Actions pour ${missionLabel}`}
          actions={secondaryActions}
          disabled={busy}
        />
      </span>
    </div>
  );
}
